import React, { useState, useCallback, useEffect } from 'react'; // Added useCallback, useEffect
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // For close icon
import { faTimes } from '@fortawesome/free-solid-svg-icons'; // Close icon
import { fadeIn } from '../../utils/animations';
import { useTranslation } from 'react-i18next';

const GalleryPageContainer = styled.div` /* Renamed */
  width: 100%;
  padding: 2rem; /* Added page padding */
  background-color: ${(props) => props.theme.colors.background}; /* Updated */
  opacity: 0; /* Initial state for page load animation */
  animation: ${fadeIn} 0.5s ease-out forwards; /* Page load animation */
`;

const ImageContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem; /* Spacing between images */
  justify-content: center; 
  align-items: center;
`;
 
interface ImageProps {
  index: number; // For animation delay
  // src and alt are passed via attrs
}

const Image = styled.img.attrs<ImageProps>(props => ({
  role: "button",
  tabIndex: 0,
  alt: props.alt, // Ensure alt is passed through
  src: props.src,   // Ensure src is passed through
}))<ImageProps>`
  width: calc(25% - 1rem); /* Responsive width for 4 columns, accounting for gap */
  aspect-ratio: 16 / 10; /* Maintain aspect ratio */
  object-fit: cover;
  cursor: pointer;
  animation: ${fadeIn} 0.5s ease-in-out;
  animation-fill-mode: both;
  border: 1px solid ${(props) => props.theme.colors.borders}; /* Updated */
  border-radius: ${(props) => props.theme.borderRadius}; /* Updated */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); /* Updated shadow */
  transition: transform 0.3s ease-out, box-shadow 0.3s ease-out;

  &:hover {
    transform: scale(1.05); /* Subtle scale effect */
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12); /* Updated hover shadow */
  }

  @media (max-width: 1200px) {
    width: calc(33.333% - 1rem); /* 3 columns */
  }
  @media (max-width: 768px) {
    width: calc(50% - 1rem); /* 2 columns */
  }
  @media (max-width: 480px) {
    width: 100%; /* 1 column */
  }

  ${({ index }) => `
  animation-delay: ${index * 200}ms;
`}
`;

// Added keydown handler for Image thumbnails
const handleImageKeyDown = (event: React.KeyboardEvent<HTMLElement>, callback: () => void) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    callback();
  }
};

const FullScreenModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.85); /* Consistent overlay */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: 0; /* Initial state for modal fade-in */
  animation: ${fadeIn} 0.3s ease-out forwards; /* Modal background fade-in */
`;

const FullScreenImage = styled.img`
  max-width: 90vw; /* Use viewport units for responsiveness */
  max-height: 90vh; /* Use viewport units for responsiveness */
  object-fit: contain;
  padding: 1rem; /* Increased padding */
  background-color: ${(props) => props.theme.colors.surface}; /* Updated */
  border-radius: ${(props) => props.theme.borderRadius}; /* Updated */
  box-shadow: 0 8px 24px rgba(0,0,0,0.15); /* More prominent modal shadow */
  animation: ${fadeIn} 0.3s ease-out; /* Faster animation for modal */
  animation-fill-mode: both;
  /* Removed index-based animation delay for FullScreenImage as it's one image at a time */
  @media (max-width: 480px) {
    max-width: 95vw;
    max-height: 95vh;
  }
`;

const CloseButtonModal = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: ${(props) => props.theme.colors.surface};
  color: ${(props) => props.theme.colors.text};
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1002; /* Above image, below other potential modal elements if any */
  transition: background-color 0.3s ease, color 0.3s ease;

  @media (max-width: 480px) {
    top: 10px;
    right: 10px;
    width: 35px;
    height: 35px;
    font-size: 1.2rem;
  }

  &:hover {
    background: ${(props) => props.theme.colors.primary};
    color: ${(props) => props.theme.colors.surface};
  }
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primary}60;
  }
`;

const ArrowButton = styled.div.attrs({ role: 'button', tabIndex: 0 })` /* Added role and tabindex */
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: ${(props) => props.theme.colors.primary}; 
  color: ${(props) => props.theme.colors.surface}; 
  padding: 0.75rem 1rem; 
  cursor: pointer;
  z-index: 1001;
  border-radius: ${(props) => props.theme.borderRadius}; 
  transition: background-color 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1); 

  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 1.2rem; /* If using text arrows, otherwise adjust padding for icon size */
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.accentHover}; 
  }
  &:focus { /* Added focus style */
    outline: none;
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primary}60;
  }
`;

// Added keydown handler for ArrowButtons
const handleArrowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, callback: () => void) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    callback();
  }
};

const LeftArrow = styled(ArrowButton)`
  left: 20px; /* Increased distance from edge */
`;

const RightArrow = styled(ArrowButton)`
  right: 20px; /* Increased distance from edge */
`;

// No explicit props
interface GalleryProps {}

const GalleryComponent: React.FC<GalleryProps> = () => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const images: string[] = [
    require('../../assets/bm_main.jpg'),
    require('../../assets/1.jpeg'),
    require('../../assets/2.jpg'),
    require('../../assets/3.jpeg'),
    require('../../assets/4.jpeg'),
    require('../../assets/7.jpg'),
    require('../../assets/8.jpg'),
    require('../../assets/9.jpg'),
    require('../../assets/10.jpg'),
    require('../../assets/11.jpg'),
    require('../../assets/12.jpg'),
    require('../../assets/13.jpg'),
  ];

  const openFullScreen = useCallback((index: React.SetStateAction<number | null>) => {
    setSelectedIndex(index);
  }, []);

  const closeFullScreen = useCallback(() => {
    setSelectedIndex(null);
  }, []);

  const goToPreviousImage = useCallback(() => {
 setSelectedIndex((prevIndex) => 
 prevIndex !== null ? (prevIndex > 0 ? prevIndex - 1 : images.length - 1) : null
 );
  }, [images.length]);

  const goToNextImage = useCallback(() => {
 setSelectedIndex((prevIndex) => 
 prevIndex !== null ? (prevIndex < images.length - 1 ? prevIndex + 1 : 0) : null
 );
  }, [images.length]);

  // Handle keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (selectedIndex === null) return;
      if (event.key === 'Escape') {
        closeFullScreen();
      } else if (event.key === 'ArrowLeft') {
        goToPreviousImage();
      } else if (event.key === 'ArrowRight') {
        goToNextImage();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedIndex, closeFullScreen, goToPreviousImage, goToNextImage]);

  return (
    <GalleryPageContainer>
      <ImageContainer>
        {images.map((image, index) => (
          <Image // Removed as="div", using attrs on styled.img instead
            key={index}
            src={image}
            alt={t('gallery.imageAlt', { index: index + 1 }) as string}
            index={index} // For animation delay
            onClick={() => openFullScreen(index)}
            onKeyDown={(e) => handleImageKeyDown(e, () => openFullScreen(index))} 
          />
        ))}
      </ImageContainer>
      {selectedIndex !== null && (
        <FullScreenModal 
          // onClick={closeFullScreen} // Removed direct click on modal to close, use button
        >
          <CloseButtonModal 
            onClick={closeFullScreen}
            onKeyDown={(e) => handleImageKeyDown(e, closeFullScreen)} // Re-use handler for Enter/Space
            aria-label={t('gallery.closeFullScreenView') as string}
          >
            <FontAwesomeIcon icon={faTimes} />
          </CloseButtonModal>
          <LeftArrow
            onClick={(e) => { e.stopPropagation(); goToPreviousImage(); }}
            onKeyDown={(e) => { e.stopPropagation(); handleArrowKeyDown(e, goToPreviousImage);}}
            aria-label={t('gallery.previousImage') as string}
          >
            &#10094;
          </LeftArrow>
          <RightArrow
            onClick={(e) => { e.stopPropagation(); goToNextImage(); }}
            onKeyDown={(e) => { e.stopPropagation(); handleArrowKeyDown(e, goToNextImage);}}
            aria-label={t('gallery.nextImage') as string}
          >
            &#10095;
          </RightArrow>
          <FullScreenImage 
            src={images[selectedIndex]}
            alt={t('gallery.fullScreenImageAlt', { index: selectedIndex + 1 }) as string}
          />
        </FullScreenModal>
      )}
    </GalleryPageContainer>
  );
}

export default React.memo(GalleryComponent);
